/**
 * +glide: the open world's gliding hints (pure; worldApp shows them):
 *  - a one-time tip the first time the hero jumps from a high place (a platform, or a drop ahead);
 *  - a discovery toast the first time the hero lands on a sky puzzle's top platform.
 */
import type { AirspaceDef, DeepReadonly, SimEvent, SkyPuzzleDef, World } from '@keystone/sim';
import { floorAt } from '@keystone/sim';

export const GLIDE_TIP = 'Hold Space in the air to glide';
/** Height over the floor (yd) that counts as "high": standing on a platform this high, or a drop this deep ahead. */
export const HIGH_YD = 3;

/** The hero just jumped from somewhere high (a platform, or ground with a big drop in front). */
export function jumpedFromHigh(events: readonly SimEvent[], world: DeepReadonly<World>, heroId: string, as: AirspaceDef | null | undefined): boolean {
  if (!as || !events.some((e) => e.type === 'jump' && e.sourceID === heroId)) return false;
  const u = world.units[heroId];
  if (!u) return false;
  const here = floorAt(as, u.pos);
  const z = u.jump?.air?.z ?? here;
  if (z - here >= HIGH_YD) return true;
  const fx = Math.cos(u.facing);
  const fy = Math.sin(u.facing);
  for (const d of [5, 9, 14]) if (floorAt(as, { x: u.pos.x + fx * d, y: u.pos.y + fy * d }) <= z - HIGH_YD - 1) return true;
  return false;
}

/** Sky puzzles whose top the hero landed on in `events`. */
export function puzzleTopsReached(events: readonly SimEvent[], heroId: string, as: AirspaceDef | null | undefined): SkyPuzzleDef[] {
  const out: SkyPuzzleDef[] = [];
  if (!as?.puzzles?.length) return out;
  for (const e of events) {
    if (e.type !== 'land' || e.sourceID !== heroId || !e.platformId) continue;
    const pz = as.puzzles.find((p) => p.topId === e.platformId);
    if (pz && !out.includes(pz)) out.push(pz);
  }
  return out;
}

/** Remembered once per browser (localStorage; tolerant of private mode). */
export function onceFlag(key: string): boolean {
  try {
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
}
